import { ContentProps } from 'structure/capital-manager/components/DesktopContent';
import { ChartsSection } from 'structure/capital-manager/components/ChartsSection';
import { Surface } from '@dapp/multi-lib/sdaoui/Surface';
import { TokenImage } from 'components/TokenImage';
import { getNetworkIconUrlByChaiId, getTokenIconUrl } from 'utils/url';
import { Body1, Header3 } from 'components/Typography';
import { CardRow } from '@dapp/multi-lib/sdaoui/CardRow';
import {
  filterIdleTokens,
  filterThirdPartyTokens,
} from 'structure/capital-manager/utils/tokens';
import { ZapNow } from 'structure/capital-manager/components/ZapNow';
import { findVaultListByDepositToken } from 'structure/capital-manager/utils/vaults';
import React, { useMemo } from 'react';
import { getNetworkByChainId } from '@dapp/multi-lib/network';
import { LegacyPositionsSection } from 'structure/capital-manager/components/LegacyPositionsSection';
import { DynaVault } from '../../../api/DynaVaultsAPI';
import { TokenAction, TokenTableRow } from 'structure/capital-manager/types';
import { formatUnits } from 'utils/units';
import { formatUsd } from '@dapp/multi-lib/lib/formatters';
import { Button } from '@dapp/multi-lib/sdaoui/Button';
import Link from 'next/link';

export const MobileContent = (props: ContentProps) => {
  const { tokens, vaultList, handleClickZap } = props;

  const idleTokens = useMemo(() => {
    return filterIdleTokens(tokens);
  }, [tokens]);

  const thirdPartyTokens = useMemo(() => {
    return filterThirdPartyTokens(tokens);
  }, [tokens]);

  return (
    <div className='flex flex-col gap-4'>
      <ChartsSection vaultList={vaultList} tokens={tokens} />
      {idleTokens.length > 0 && (
        <>
          <Header3>Idle Tokens</Header3>
          <TokenCardList
            tokens={idleTokens}
            vaultList={vaultList}
            handleClickZap={handleClickZap}
          />
        </>
      )}

      {thirdPartyTokens.length > 0 && (
        <>
          <Header3>Third Party Tokens</Header3>
          <TokenCardList
            tokens={thirdPartyTokens}
            vaultList={vaultList}
            handleClickZap={handleClickZap}
          />
        </>
      )}
      <LegacyPositionsSection />
    </div>
  );
};

interface TokenCardListProps {
  tokens: TokenTableRow[];
  vaultList: DynaVault[];
  handleClickZap: (params: {
    depositTokenAddress: `0x${string}`;
    vault: DynaVault;
  }) => void;
}

const TokenCardList = (props: TokenCardListProps) => {
  const { tokens, vaultList, handleClickZap } = props;
  return (
    <div className='flex flex-col gap-4'>
      {tokens.map((token) => (
        <TokenCard
          key={token.id}
          token={token}
          vaultList={vaultList}
          handleClickZap={handleClickZap}
        />
      ))}
    </div>
  );
};

interface TokenCardProps {
  token: TokenTableRow;
  vaultList: DynaVault[];
  handleClickZap: (params: {
    depositTokenAddress: `0x${string}`;
    vault: DynaVault;
  }) => void;
}

const TokenCard = (props: TokenCardProps) => {
  const { token, vaultList, handleClickZap } = props;
  return (
    <Surface
      rounded='medium'
      style='gradient'
      className='flex flex-col border-[#616F8B66]/40 p-5'
      outline='default'
    >
      <div className='flex flex-col gap-3'>
        <div className='flex flex-row items-center gap-2'>
          <div className='h-[24px] w-[24px]'>
            <TokenImage
              symbol={token.symbol}
              src={getTokenIconUrl(token.symbol)}
              height='32'
              width='32'
              alt={token.symbol}
            />
          </div>
          <Body1 className='font-bold'>{token.name}</Body1>
        </div>

        <CardRow>
          <Body1 className='text-[1.125rem] leading-[1.125rem] text-white-500'>
            Network
          </Body1>
          <div className='flex flex-row items-center gap-1'>
            <Body1 className='text-[1rem]'>
              {getNetworkByChainId(token.chainId)?.name}
            </Body1>
            <div className='flex min-h-[24px] min-w-[24px]'>
              <TokenImage
                symbol={getNetworkByChainId(token.chainId)?.shortName ?? ''}
                src={getNetworkIconUrlByChaiId(token.chainId)}
                alt={getNetworkByChainId(token.chainId)?.shortName ?? ''}
                width={24}
                height={24}
              />
            </div>
          </div>
        </CardRow>

        <CardRow className='items-start'>
          <Body1 className='text-[1.125rem] leading-[1.125rem] text-white-500'>
            Balance:
          </Body1>
          <div className='flex flex-row flex-wrap justify-end gap-1'>
            <Body1 className='text-[1.125rem] font-bold leading-[1.125rem] tracking-[0.4px]'>
              {formatUnits(token.balance, token.decimals)}
            </Body1>
            <Body1 className='text-[1.125rem] font-bold leading-[1.125rem] tracking-[0.4px]'>
              {token.symbol}
            </Body1>
          </div>
        </CardRow>
        <CardRow>
          <Body1 className='text-[1.125rem] leading-[1.125rem] text-white-500'>
            USD Value:
          </Body1>
          <Body1 className='text-[1.125rem] font-bold leading-[1.125rem] tracking-[0.4px]'>
            ${formatUsd(token.balanceUsd)}
          </Body1>
        </CardRow>

        {token.tokenAction === TokenAction.Zap && (
          <ZapNow
            className='mt-6 flex-col items-stretch'
            depositTokenAddress={token.address}
            vaultList={findVaultListByDepositToken(vaultList, token.address)}
            onZap={handleClickZap}
          />
        )}
        {token.tokenAction === TokenAction.BuySFI && (
          <Button
            component={Link}
            className='inline-flex shrink-0 items-center gap-2 text-[1.125rem] lg:text-[1rem]'
            href={token.buySFITokenHref}
            target='_blank'
          >
            Buy SFI
          </Button>
        )}

        {token.tokenAction === TokenAction.Stake && (
          <Button
            component={Link}
            className='inline-flex shrink-0 items-center gap-2 text-[1.125rem] lg:text-[1rem]'
            href={`/staking/${token.symbol}`}
            target='_blank'
          >
            Stake {token.symbol}
          </Button>
        )}

        {token.tokenAction === TokenAction.Migrate && (
          <Button
            component={Link}
            className='inline-flex shrink-0 items-center gap-2 text-[1.125rem] lg:text-[1rem]'
            href={`/migrate`}
            target='_blank'
          >
            Migrate {token.symbol}
          </Button>
        )}
      </div>
    </Surface>
  );
};
